import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
    Plus, Edit3, Trash2, ArrowLeft, Play, Clock,
    Search, MoreVertical, Eye, GripVertical
} from 'lucide-react'
import { useToast } from '../hooks/useToast'
import { supabase } from '../services/supabase'
import Button from '../components/Button'
import ModalAula from '../components/ModalAula'
import { ToastContainer } from '../components/Toast'
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    type DragEndEvent
} from '@dnd-kit/core'
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

interface Curso {
    id: string
    titulo: string
}

interface Modulo {
    id: string
    titulo: string
    descricao: string | null
}

interface Aula {
    id: string
    titulo: string
    descricao: string | null
    video_url: string | null
    duracao: number | null
    ordem_exibicao: number
    status: string
    created_at: string
    updated_at: string
}

interface SortableAulaItemProps {
    aula: Aula
    openDropdown: string | null
    setOpenDropdown: (id: string | null) => void
    setEditingAula: (aula: Aula) => void
    setIsModalOpen: (open: boolean) => void
    openDeleteModal: (aula: Aula) => void
    formatarDuracao: (minutos: number | null) => string
    getStatusColor: (status: string) => string
}

function SortableAulaItem({
    aula,
    openDropdown,
    setOpenDropdown,
    setEditingAula,
    setIsModalOpen,
    openDeleteModal,
    formatarDuracao,
    getStatusColor
}: SortableAulaItemProps) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ id: aula.id })

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1
    }

    return (
        <div
            ref={setNodeRef}
            style={style}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-lg transition-all"
        >
            <div className="p-6">
                <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4 flex-1">
                        {/* Handle de arrastar */}
                        <button
                            {...attributes}
                            {...listeners}
                            className="cursor-grab active:cursor-grabbing p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                        >
                            <GripVertical className="text-gray-400 dark:text-gray-500" size={20} />
                        </button>

                        <div className="flex-shrink-0 w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                            <span className="text-purple-600 dark:text-purple-400 font-semibold text-lg">
                                {aula.ordem_exibicao}
                            </span>
                        </div>
                        <div className="flex-1">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                                {aula.titulo}
                            </h3>
                            {aula.descricao && (
                                <p className="text-gray-600 dark:text-gray-400 text-sm mb-3">
                                    {aula.descricao}
                                </p>
                            )}
                            <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                                <div className="flex items-center gap-1">
                                    <Clock className="h-4 w-4" />
                                    <span>{formatarDuracao(aula.duracao)}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                    <Play className="h-4 w-4" />
                                    <span>Aula {aula.ordem_exibicao}</span>
                                </div>
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(aula.status)}`}>
                                    {aula.status}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        {aula.video_url && (
                            <Button
                                variant="btn_secondary"
                                size="sm"
                                onClick={() => aula.video_url && window.open(aula.video_url, '_blank')}
                                className="flex items-center gap-2"
                            >
                                <Eye className="h-4 w-4" />
                                Ver Vídeo
                            </Button>
                        )}

                        <div className="relative">
                            <button
                                onClick={() => {
                                    setOpenDropdown(openDropdown === aula.id ? null : aula.id)
                                }}
                                className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium transition-colors duration-200 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-300 p-2 rounded"
                            >
                                <MoreVertical className="h-4 w-4" />
                            </button>

                            {/* Dropdown Menu */}
                            {openDropdown === aula.id && (
                                <div
                                    className="absolute right-0 top-8 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-[9999] min-w-[160px] pointer-events-auto"
                                    onClick={(e) => {
                                        e.stopPropagation()
                                    }}
                                >
                                    <div className="py-1">
                                        <button
                                            onMouseDown={(e) => {
                                                e.preventDefault()
                                                e.stopPropagation()
                                            }}
                                            onClick={(e) => {
                                                e.preventDefault()
                                                e.stopPropagation()
                                                console.log('✏️ Abrindo modal para EDITAR aula:', aula)
                                                setIsModalOpen(true)
                                                setEditingAula(aula)
                                                setOpenDropdown(null)
                                            }}
                                            className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 cursor-pointer"
                                        >
                                            <Edit3 className="h-4 w-4" />
                                            Editar Aula
                                        </button>
                                        <button
                                            onMouseDown={(e) => {
                                                e.preventDefault()
                                                e.stopPropagation()
                                                openDeleteModal(aula)
                                                setOpenDropdown(null)
                                            }}
                                            onClick={(e) => {
                                                e.preventDefault()
                                                e.stopPropagation()
                                                openDeleteModal(aula)
                                                setOpenDropdown(null)
                                            }}
                                            className="w-full px-4 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2 cursor-pointer"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                            Excluir Aula
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default function AdminAulas() {
    const { cursoId, moduloId } = useParams<{ cursoId: string; moduloId: string }>()
    const navigate = useNavigate()
    const { showSuccess, showError, toasts } = useToast()
    const [curso, setCurso] = useState<Curso | null>(null)
    const [modulo, setModulo] = useState<Modulo | null>(null)
    const [aulas, setAulas] = useState<Aula[]>([])
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [editingAula, setEditingAulaState] = useState<Aula | null>(null)
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
    const [aulaToDelete, setAulaToDelete] = useState<Aula | null>(null)
    const [openDropdown, setOpenDropdown] = useState<string | null>(null)

    // Wrapper para logar sempre que editingAula mudar
    const setEditingAula = useCallback((aula: Aula | null) => {
        console.log('🎯 setEditingAula chamado com:', aula ? `${aula.titulo} (ID: ${aula.id})` : 'null')
        console.trace('📍 Stack trace de onde foi chamado:')
        setEditingAulaState(aula)
    }, [])

    // Configurar sensores para drag and drop
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    )

    // Handler para quando o drag termina
    const handleDragEnd = async (event: DragEndEvent) => {
        const { active, over } = event

        if (!over || active.id === over.id) {
            return
        }

        const oldIndex = aulas.findIndex(aula => aula.id === active.id)
        const newIndex = aulas.findIndex(aula => aula.id === over.id)

        if (oldIndex === -1 || newIndex === -1) {
            return
        }

        // Reordenar a lista localmente
        const newAulas = arrayMove(aulas, oldIndex, newIndex)
        setAulas(newAulas)

        // Atualizar ordem_exibicao no banco de dados
        try {
            const updates = newAulas.map((aula, index) => ({
                id: aula.id,
                ordem_exibicao: index + 1
            }))

            // Executar atualizações em lote
            for (const update of updates) {
                const { error } = await supabase
                    .from('aulas')
                    .update({ ordem_exibicao: update.ordem_exibicao })
                    .eq('id', update.id)

                if (error) {
                    console.error('Erro ao atualizar ordem:', error)
                    throw error
                }
            }

            showSuccess('Ordem das aulas atualizada com sucesso!')
        } catch (error) {
            console.error('Erro ao atualizar ordem das aulas:', error)
            showError('Erro ao atualizar ordem das aulas. Tente novamente.')
            // Recarregar lista original em caso de erro
            loadAulas()
        }
    }

    useEffect(() => {
        if (cursoId && moduloId) {
            loadCurso()
            loadModulo()
            loadAulas()
        }
    }, [cursoId, moduloId])

    // Fechar dropdown ao clicar fora
    useEffect(() => {
        const handleClickOutside = () => {
            if (openDropdown) {
                setOpenDropdown(null)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [openDropdown])

    // Monitorar mudanças no estado do modal
    useEffect(() => {
        console.log('AdminAulas: Estado do modal mudou:', { isModalOpen, editingAula })
    }, [isModalOpen, editingAula])

    const loadCurso = async () => {
        try {
            const { data, error } = await supabase
                .from('cursos')
                .select('id, titulo')
                .eq('id', cursoId)
                .single()

            if (error) throw error
            setCurso(data)
        } catch (err) {
            console.error('Erro ao carregar curso:', err)
            showError('Erro ao carregar informações do curso.')
        }
    }

    const loadModulo = async () => {
        try {
            const { data, error } = await supabase
                .from('modulos')
                .select('id, titulo, descricao')
                .eq('id', moduloId)
                .single()

            if (error) throw error
            setModulo(data)
        } catch (err) {
            console.error('Erro ao carregar módulo:', err)
            showError('Erro ao carregar informações do módulo.')
        }
    }

    const loadAulas = useCallback(async (aulaIdToUpdate?: string) => {
        try {
            setLoading(true)
            const { data, error } = await supabase
                .from('aulas')
                .select('*')
                .eq('modulo_id', moduloId)
                .order('ordem_exibicao')

            if (error) throw error
            setAulas(data || [])
            
            // Atualizar editingAula se houver uma aula sendo editada
            if (aulaIdToUpdate && data) {
                console.log('🔄 Atualizando editingAula com ID:', aulaIdToUpdate)
                const updatedAula = data.find(a => a.id === aulaIdToUpdate)
                if (updatedAula) {
                    console.log('✅ editingAula atualizado:', updatedAula)
                    setEditingAula(updatedAula)
                } else {
                    console.log('⚠️ Aula não encontrada na lista')
                }
            }
        } catch (err) {
            console.error('Erro ao carregar aulas:', err)
            showError('Erro ao carregar aulas. Tente novamente.')
        } finally {
            setLoading(false)
        }
    }, [moduloId, showError])

    const handleDelete = async () => {
        if (!aulaToDelete) return

        try {
            const { error } = await supabase
                .from('aulas')
                .delete()
                .eq('id', aulaToDelete.id)

            if (error) throw error

            showSuccess('Aula excluída com sucesso!')
            loadAulas()
        } catch (err) {
            showError('Erro ao excluir aula. Tente novamente.')
        } finally {
            setIsDeleteModalOpen(false)
            setAulaToDelete(null)
        }
    }

    const openDeleteModal = (aula: Aula) => {
        setAulaToDelete(aula)
        setIsDeleteModalOpen(true)
    }

    const closeDeleteModal = () => {
        setIsDeleteModalOpen(false)
        setAulaToDelete(null)
    }

    const formatarDuracao = (minutos: number | null) => {
        if (!minutos) return 'N/A'
        const horas = Math.floor(minutos / 60)
        const mins = minutos % 60
        if (horas > 0) {
            return `${horas}h ${mins}min`
        }
        return `${mins}min`
    }

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'ativo':
                return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
            case 'inativo':
                return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
            default:
                return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400'
        }
    }

    const filteredAulas = aulas.filter(aula =>
        aula.titulo.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (aula.descricao && aula.descricao.toLowerCase().includes(searchTerm.toLowerCase()))
    )

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="animate-pulse">
                        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-8"></div>
                        <div className="space-y-4">
                            {[1, 2, 3].map((i) => (
                                <div key={i} className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-4"></div>
                                    <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-full mb-2"></div>
                                    <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-2/3"></div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    if (!curso || !modulo) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="text-center py-16">
                        <Play className="mx-auto h-24 w-24 text-gray-400 dark:text-gray-500 mb-6" />
                        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                            Módulo não encontrado
                        </h1>
                        <Button
                            onClick={() => navigate('/admin/cursos')}
                            variant="btn_secondary"
                            className="px-8 py-3"
                        >
                            Voltar aos Cursos
                        </Button>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-8">
                    <Button
                        onClick={() => navigate(`/admin/cursos/${cursoId}/modulos`, { 
                            state: { moduloId: moduloId } 
                        })}
                        variant="btn_secondary"
                        className="mb-4 flex items-center gap-2"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        Voltar aos Módulos
                    </Button>

                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                                Aulas do Módulo
                            </h1>
                            <p className="text-gray-600 dark:text-gray-400">
                                {curso.titulo} - {modulo.titulo}
                            </p>
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => {
                                    console.log('AdminAulas: CLICK NO BOTÃO NOVA AULA!')
                                    console.log('AdminAulas: Estado antes:', { isModalOpen, editingAula })
                                    setEditingAula(null)
                                    setIsModalOpen(true)
                                    console.log('AdminAulas: Estados atualizados para nova aula!')
                                }}
                                className="bg-[rgb(235,106,59)] hover:bg-[rgb(220,95,50)] text-white font-medium transition-colors duration-200 px-4 py-2 rounded-lg flex items-center gap-2"
                            >
                                <Plus size={20} />
                                Nova Aula
                            </button>

                        </div>
                    </div>
                </div>

                {/* Filtros */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" size={20} />
                        <input
                            type="text"
                            placeholder="Buscar aulas..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-10 pr-4 py-2 w-full border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
                        />
                    </div>
                </div>

                {filteredAulas.length === 0 ? (
                    <div className="text-center py-16">
                        <Play className="mx-auto h-24 w-24 text-gray-400 dark:text-gray-500 mb-6" />
                        <h3 className="text-xl font-medium text-gray-900 dark:text-gray-100 mb-2">
                            Nenhuma aula encontrada
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400 mb-8">
                            {searchTerm
                                ? 'Tente ajustar os termos de busca.'
                                : 'Crie sua primeira aula para começar.'
                            }
                        </p>
                        {!searchTerm && (
                            <Button
                                onClick={() => {
                                    setEditingAula(null)
                                    setIsModalOpen(true)
                                    setIsModalOpen(true)
                                }}
                                variant="btn_add"
                                className="px-8 py-3"
                            >
                                Criar Primeira Aula
                            </Button>
                        )}
                    </div>
                ) : (
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                    >
                        <SortableContext
                            items={filteredAulas.map(aula => aula.id)}
                            strategy={verticalListSortingStrategy}
                        >
                            <div className="space-y-4">
                                {filteredAulas.map((aula) => (
                                    <SortableAulaItem
                                        key={aula.id}
                                        aula={aula}
                                        openDropdown={openDropdown}
                                        setOpenDropdown={setOpenDropdown}
                                        setEditingAula={setEditingAula}
                                        setIsModalOpen={setIsModalOpen}
                                        openDeleteModal={openDeleteModal}
                                        formatarDuracao={formatarDuracao}
                                        getStatusColor={getStatusColor}
                                    />
                                ))}
                            </div>
                        </SortableContext>
                    </DndContext>
                )}

                {/* Modal de Exclusão */}
                {isDeleteModalOpen && aulaToDelete && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
                            <div className="p-6">
                                <div className="flex items-center mb-4">
                                    <div className="flex-shrink-0 w-10 h-10 mx-auto bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                                        <Trash2 className="w-6 h-6 text-red-600 dark:text-red-400" />
                                    </div>
                                </div>
                                <div className="text-center">
                                    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                                        Excluir Aula
                                    </h3>
                                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                                        Tem certeza que deseja excluir a aula <strong>"{aulaToDelete.titulo}"</strong>?
                                        Esta ação não pode ser desfeita.
                                    </p>
                                    <div className="flex gap-3 justify-center">
                                        <Button
                                            variant="btn_secondary"
                                            onClick={closeDeleteModal}
                                        >
                                            Cancelar
                                        </Button>
                                        <Button
                                            variant="btn_secondary"
                                            onClick={handleDelete}
                                            className="bg-red-600 hover:bg-red-700 text-white"
                                        >
                                            Excluir
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Modal de Aula */}
                <ModalAula
                    isOpen={isModalOpen}
                    onClose={() => {
                        console.log('🚪 onClose do modal chamado')
                        setIsModalOpen(false)
                        setEditingAula(null)
                    }}
                    onSuccess={() => {
                        console.log('✅ onSuccess do modal chamado')
                        void loadAulas()
                        setIsModalOpen(false)
                        setEditingAula(null)
                    }}
                    onUpdateList={loadAulas}
                    aula={editingAula}
                    moduloId={moduloId!}
                    showSuccess={showSuccess}
                    showError={showError}
                />

                {/* Toast Container */}
                <ToastContainer toasts={toasts} />
            </div>
        </div>
    )
}
